import React from 'react'

import { styled } from '@mui/material/styles'

import NoImageIcon from '../../image/product.png'
import { OrderProduct } from '../../entities/order-product/OrderProduct'
import ProductItemControls from '../product-item-controls/ProductItemControlsContainer'
import ProductInProgressModal from '../product-in-progress-modal/ProductInProgressModalContainer'
import ProductLabel from '../product-label/ProductLabel'

import ProductStatus from '../product-status/ProductStatus'
import ProductIncomingModal from '../product-incoming-modal/ProductIncomingModalContainer'
import GotIt from '../got-it/GotItContainer'

export interface IOwnProps {
    className?: string
    product: OrderProduct
    shouldShowControls?: boolean
    hasNextConsolidatedProduct?: boolean
    shouldOpenModal?: boolean
    isModal?: boolean
}

export type IProps = IOwnProps

export interface ProductProps {
    isDeclined: boolean
    isCanceled: boolean
}

const Product = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isDeclined' && prop !== 'isCanceled',
})<ProductProps>(({ isDeclined, isCanceled }) => ({
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    ...(isDeclined && {
        opacity: 0.6,
        textDecoration: 'line-through',
    }),
    ...(isCanceled && {
        textDecoration: 'line-through',
    }),
}))

const ProductCount = styled('div')(({ theme }) => ({
    flexBasis: 35,
    flexShrink: 0,
    fontSize: 18,

    fontWeight: 700,
    marginRight: theme.spacing(2),
    whiteSpace: 'nowrap',
}))

const ProductImageWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginRight: theme.spacing(),
    flexGrow: 1,
}))

const ProductImage = styled('img')({
    display: 'block',
    marginRight: 8,
})

const ProductInfo = styled('div')({})

const ProductName = styled('h5')(({ theme }) => ({
    margin: 0,
    marginBottom: theme.spacing(),
    fontSize: 14,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    '& span': {
        marginRight: theme.spacing(),
        display: '-webkit-box',
        overflow: 'hidden',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        flexGrow: 1,
    },
}))

export interface ProductOptionsProps {
    isModal: boolean
}

const ProductOptions = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isModal',
})<ProductOptionsProps>(({ theme, isModal }) => ({
    margin: 0,
    fontSize: 12,
    color: theme.palette.greyColor.light,
    paddingRight: theme.spacing(2),
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    '& p': {
        margin: 0,
    },
    ...(!isModal && {
        color: theme.palette.greyColor.main,
        WebkitLineClamp: 2,
    }),
}))

const ProductCode = styled('p')(({ theme }) => ({
    fontSize: 12,
    color: theme.palette.greyColor.light,
    textTransform: 'none',
    margin: 0,
}))

const Toolbar = styled('div')({
    flexShrink: 0,
    marginLeft: 'auto',
})

const LabelContainer = styled('div')({
    flexShrink: 0,
    marginLeft: 'auto',
})

interface IProductWrapper {
    product: OrderProduct
    className?: string
    shouldOpenModal?: boolean
}

const ProductWrapper: React.FC<IProductWrapper> = (props) => {
    const { product, children, className, shouldOpenModal = true } = props

    const [isModalOpen, setIsModalOpen] = React.useState(false)
    const canOpenModal = shouldOpenModal && !product.isCanceled

    const handleClick = () => {
        setIsModalOpen(true)
    }

    const handleModalClose = () => {
        setIsModalOpen(false)
    }

    if (product.isInProgress() && canOpenModal) {
        return (
            <>
                <div className={className} onClick={handleClick}>
                    {children}
                </div>

                <ProductInProgressModal
                    onClose={handleModalClose}
                    product={product}
                    open={isModalOpen}
                />
            </>
        )
    }

    if (product.canMoveToInProgress() && canOpenModal) {
        return (
            <>
                <div className={className} onClick={handleClick}>
                    {children}
                </div>

                <ProductIncomingModal
                    onClose={handleModalClose}
                    product={product}
                    open={isModalOpen}
                />
            </>
        )
    }

    return <div className={className}>{children}</div>
}

const ProductWrapperStyled = styled(ProductWrapper)({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '100%',
})

const isEmpty = (value: string | undefined): boolean => {
    return value?.trim() ? false : true
}

const ProductItem: React.FC<IProps> = (props) => {
    const {
        className,
        product,
        shouldShowControls = true,
        hasNextConsolidatedProduct = false,
        shouldOpenModal = true,
        isModal = false,
    } = props

    const [imageRef, setImageRef] = React.useState<HTMLImageElement | null>(
        null
    )
    const [isFailed, setIsFailed] = React.useState(false)

    const imageSrc = isFailed ? NoImageIcon : product.imageUrl || NoImageIcon

    const handleFailedLoading = (event: ErrorEvent) => {
        if (event.target) {
            setIsFailed(true)
        }
    }

    React.useEffect(() => {
        if (imageRef) {
            imageRef.addEventListener('error', handleFailedLoading)
        }

        return () => {
            if (imageRef) {
                imageRef.removeEventListener('error', handleFailedLoading)
            }
        }
    }, [imageRef])

    return (
        <div className={className}>
            <ProductWrapperStyled
                product={product}
                shouldOpenModal={shouldOpenModal}
            >
                <Product
                    isDeclined={product.isDeclined}
                    isCanceled={product.isCanceled}
                >
                    <ProductCount>{product.quantity} X</ProductCount>
                    <ProductImageWrapper>
                        <ProductImage
                            src={imageSrc}
                            alt={product.name}
                            width={35}
                            height={35}
                            ref={(ProductImage) => setImageRef(ProductImage)}
                        />
                        <ProductInfo>
                            <ProductName>
                                <span>{product.name}</span>
                                <div>
                                    <ProductStatus
                                        status={{
                                            code: product.statusCode,
                                            text: product.status,
                                        }}
                                    />
                                </div>
                                {product.isCanceled && (
                                    <div>
                                        <GotIt
                                            orderId={product.order.orderId}
                                            itemId={product.itemId}
                                            customWay={
                                                isModal
                                                    ? 'from-modal'
                                                    : 'from-column'
                                            }
                                        />
                                    </div>
                                )}
                            </ProductName>
                            <ProductOptions isModal={isModal}>
                                {Object.entries(product.options)
                                    .map((item) =>
                                        item.join(isEmpty(item[1]) ? '' : ': ')
                                    )
                                    .join('  ')}
                                <p>{product.shortDescription}</p>
                            </ProductOptions>
                            {isModal && (
                                <ProductCode>{product.code}</ProductCode>
                            )}
                        </ProductInfo>
                    </ProductImageWrapper>
                </Product>

                {product.label && !product.order?.cycle?.isInIncoming && (
                    <LabelContainer>
                        <ProductLabel
                            label={product.label}
                            showLink={hasNextConsolidatedProduct}
                        />
                    </LabelContainer>
                )}
            </ProductWrapperStyled>

            {shouldShowControls && !product.isCanceled && (
                <Toolbar>
                    <ProductItemControls product={product} />
                </Toolbar>
            )}
        </div>
    )
}

export default styled(ProductItem)({
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
})
